import React
    , {
    useCallback,
    useReducer,
    useState
} from "react";
import formRendererReducer
    from "../../reducers/formRendererReducer";
import FormTemplateHelpers
    from '@helpers/form-template-helpers';
import DbTableHelpers
    from "@helpers/dbtable-helpers";
import SubmitSuccess
    from "./SubmitSuccess";
import FormValidationHelpers
    from "@helpers/form-validation-helpers";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {AdapterDayjs}
    from "@mui/x-date-pickers-pro/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers-pro";

const FormData = React.createContext({
    state: {
        template: {},
        values: {},
        events: [],
        fields: [],
        isWebform: false,
        origin: 'https://service.formitize.com',
        submitting: false,
        loading: false,
        submitted: false
    },
    dispatch: () => {
    },
    /**
     * Retrieve the answer from the state's stored values for a given field (if any)
     */
    findAnswer: () => {
    }
});


function generateMissingValues(values, allFields) {
    const valueKeys = Object.keys(values);
    const missing = allFields.filter(field => valueKeys.indexOf(field.id) === -1);

    if (missing.length > 0) {
        for (const field of missing) {
            if (FormTemplateHelpers.fieldHasRepeatableParent(field) && field.parent.objecttype !== 'formSubheader'  && field.parent.objecttype !== 'formRow') {
                continue;
            }

            /**
             * "0": {
                    "id": "FBCD1FCF-C118-EF7F-4A72-B134676F2B60",
                    "repeatedCount": 0,
                    "type": "formSubheader",
                    "value": {},
                    "visible": true,
                    "objectname": "formSubheader_2"
                }
             */
            const index = FormTemplateHelpers.fieldHasRepeatableParent(field) && field.parent.objecttype !== 'formSubheader' ? "0_1" : "0";
            // console.log({
            //     index,
            //     field
            // })

            let value = {};
            if (field.objecttype === 'formMultiple' && field.content?.choiceType === 'dropdown') {
                value = {[Object.keys(field.content.value)[0]]: Object.values(field.content.value).length > 0 ? Object.values(field.content.value)[0].item : ''};
            }

            values[field.id] = {
                [index]: {
                    id: field.id,
                    repeatedCount: 0,
                    type: field.objecttype,
                    value: value,
                    visible: field?.content?.visible === true,
                    required: field?.content?.required === true,
                    minLength: field?.content?.minLength,
                    maxLength: field?.content?.maxLength,
                    objectname: field.name
                }
            }
        }
    }

    return values;
}

/**
 * Provides the Form Renderer with the state context necessary to render the Form
 *
 * @param {object} template - Form Template object
 * @param {object} submission - Submission object (if any)
 * @param {object} defaultAnswers   - Default answers object (if any)
 * @param {string} origin - Origin of the form (used for CORS). E.g. https://service.formitize.com
 * @param {boolean} isWebform
 * @param formRenderer
 * @param companyID
 * @param formID
 * @returns {JSX.Element}
 * @constructor
 */
const FormProvider = ({
                          template = {},
                          submission = {},
                          defaultAnswers = {},
                          origin = "https://service.formitize.com",
                          isWebform = false,
                          formRenderer = () => {
                          },
                          companyID,
                          formID,
                      }) => {
    const [validationErrors, setValidationErrors] = useState([]);
    let values = Object.keys(submission).length > 0 ? {...submission} : {...defaultAnswers};
    const allFields = FormTemplateHelpers.getFields(template);
    const events = FormTemplateHelpers.buildEventsArray(allFields);
    const {executeRecaptcha} = useGoogleReCaptcha();

    values = generateMissingValues(values, allFields);

    /**
     * state represents the current state of the form, including field values and event driven changes (such as visibility and required attribute changes).
     *
     * values:  Contains state of all form field values. Initially populated with the previous submission (if any).
     * template: Contains the state of the form template. Initially populated with the form template, but can be modified by event driven changes (e.g. visibility changes).
     */
    const [state, dispatch] = useReducer(formRendererReducer, {
        values: {
            ...values,
        },
        template: {
            ...template
        },
        events: [
            ...events
        ],
        fields: [
            ...allFields
        ],
        isWebform,
        origin,
        submitting: false,
        loading: false,
        submitted: false
    });

    /**
     * Retrieve the answer (if any) from the state's stored values for a given field
     *
     * @param id
     * @param index
     * @returns {*}
     */
    const findAnswer = (id, index = "0") => {
        return state.values[id] && state.values[id][index] ? state.values[id][index] : {};
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidationErrors([]);

        if (!formID) {
            setValidationErrors(["Missing Form ID"]);
        }

        if (!companyID) {
            setValidationErrors(["Missing Company ID"]);
        }

        dispatch({
            type: 'START_SUBMIT',
            payload: {}
        });

        const validationResults = FormValidationHelpers.validateFields(state.values, state.fields, (field, answerIndex, errors) => {
            dispatch({
                type: "SET_ERROR",
                payload: {
                    field,
                    answerIndex,
                    value: errors
                }
            })
        });

        if (validationResults !== true) {
            dispatch({
                type: 'FINISH_SUBMIT',
                payload: {}
            });

            setValidationErrors(validationResults);

            return false;
        }

        const recaptchaToken = await getRecaptchaToken();

        DbTableHelpers.fetchAsync({
            url: `${origin}forms?id=${formID}&company=${companyID}`,
            post: state.isWebform ? 'editWeb' : 'editV3',
            type: "form/submitted",
            global: false,
            data: {
                isWeb: state.isWebform ? '1' : '',
                data: JSON.stringify(state.values),
                recaptchaToken,
            }
        }).then(async (response) => {
            const r = await response.json();

            if (r.error && r.error.length > 0) {
                throw new Error(`${r.error}`);
            }

            if (r.redirectOnSubmit && r.redir) {
                return window.location.href = r.redir;
            }

            dispatch({
                type: 'SET_SUBMITTED',
                payload: {}
            });
        }).catch((error) => {
            setValidationErrors([error.toString()]);
        }).finally(() => {
            dispatch({
                type: 'FINISH_SUBMIT',
                payload: {}
            });
        });
    }

    const handleOnRestart = (e) => {
        e.preventDefault();

        dispatch({
            type: 'RESTART_FORM',
            payload: {
                submission: {...values}
            }
        });
    }

    // Create an event handler so you can call the verification on button click event or form submit
    const getRecaptchaToken = useCallback(async () => {
        if (!executeRecaptcha) {
            // @TODO Gracefully handle error
            console.log('Execute recaptcha not yet available');
            return;
        }

        const token = await executeRecaptcha('submit');

        return token;
    }, [executeRecaptcha]);

    return (
        <FormData.Provider value={{
            state,
            dispatch,
            findAnswer
        }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div className='fz-form-provider'>
                    {!state.submitted && formRenderer({
                        values: state.values,
                        template: state.template,
                        defaultAnswers, // Pass in the default answers as-is - they're never mutated by child components and therefore don't belong in state.
                        onSubmit: handleSubmit,
                        errors: validationErrors
                    })}

                    {state.submitted &&
                    <SubmitSuccess origin={origin} onBack={handleOnRestart}/>}
                </div>
            </LocalizationProvider>
        </FormData.Provider>
    )
}

export default FormProvider;

export {
    FormData
}